import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import {
  clubBenefitCards,
  clubHeroPublicLine,
  clubHeroTitleLine,
  clubJoinCtaLine,
  clubPriceLine,
  thresholdFigure,
} from '../../domain/club';
import type { MembershipEntryPayload } from '../../domain/clubEntry';
import type { MembershipFormatters } from '../../domain/format';
import type { MembershipPlan } from '../../domain/types';
import { useViewedOnce } from '../useViewedOnce';
import { ClubPlanTerms } from './ClubPlanTerms';
import { CLUB_CLASS } from './clubStyles';
import { reportEntryViewed, useOpenClubJoin } from './useClubEntry';

export interface ClubHeroFigureProps {
  thresholdMinor: number | null;
  className?: string;
}

/** The big translucent threshold figure in the bottom right corner of a hero. */
export const ClubHeroFigure = ({
  thresholdMinor,
  className,
}: ClubHeroFigureProps) => {
  const figure = thresholdFigure(thresholdMinor);
  return figure ? (
    <span
      aria-hidden="true"
      className={cn(CLUB_CLASS.figure, className, 'leading-none')}
    >
      {figure}
    </span>
  ) : null;
};

export interface ClubOfferScreenProps {
  plan: MembershipPlan;
  formatters: MembershipFormatters;
}

/**
 * The club page of a non-member: the burgundy hero on the zone's best plan
 * with its main button ("Start my free month" or "Join CityDrinks Club"), then the benefit cards. Reports
 * `Membership: Entry Viewed` and `Entry Clicked` with `source: club_page`.
 */
export const ClubOfferScreen = observer(
  ({ plan, formatters }: ClubOfferScreenProps) => {
    const { t } = useTranslation();
    const openJoin = useOpenClubJoin();
    const title = clubHeroTitleLine(plan, formatters);
    const publicLine = clubHeroPublicLine(plan.currency, formatters);
    const cards = clubBenefitCards(plan, formatters);
    const price = clubPriceLine(plan, formatters);
    const cta = clubJoinCtaLine(plan);
    const payload: MembershipEntryPayload = {
      source: 'club_page',
      planCode: plan.code,
    };

    useViewedOnce(payload, reportEntryViewed);

    return (
      <div className="flex flex-col desktop:gap-4 gap-3">
        <section
          className={cn(
            'relative flex desktop:min-h-[400px] flex-col justify-between desktop:gap-10 gap-6 overflow-hidden desktop:rounded-[32px] rounded-[28px] desktop:px-12 px-5 desktop:py-10 py-6 text-white',
            CLUB_CLASS.hero,
          )}
        >
          <ClubHeroFigure
            thresholdMinor={plan.freeDeliveryThresholdMinor}
            className="-right-[18px] desktop:-right-6 -bottom-[52px] desktop:-bottom-16 desktop:text-[260px] text-[180px]"
          />
          <div className="relative flex flex-col desktop:gap-5 gap-3">
            <h2
              className={cn(
                CLUB_CLASS.heading,
                'desktop:max-w-[700px] max-w-[620px] desktop:text-[56px] text-[34px] text-white! desktop:leading-[58px] leading-[38px] [text-wrap:balance] desktop:[text-wrap:wrap]',
              )}
            >
              {t(title.key, title.params)}
            </h2>
            <p className="max-w-[520px] desktop:text-[18px] text-[#ffffffd9] text-[15px] desktop:leading-[26px] leading-[22px]">
              {t(publicLine.key, publicLine.params)}
            </p>
          </div>
          <div className="relative flex desktop:flex-row flex-col desktop:items-center items-stretch desktop:gap-5 gap-2.5">
            <button
              type="button"
              className={cn(
                CLUB_CLASS.white,
                'desktop:h-14 h-[52px] desktop:w-auto desktop:self-auto desktop:leading-6 leading-5 md:w-[360px] md:self-center',
              )}
              onClick={() => openJoin('club_page', plan.code)}
            >
              {t(cta.key, cta.params)}
            </button>
            <p
              className={cn(
                'text-center desktop:text-start desktop:text-[16px] text-[13px] desktop:leading-5 leading-[18px]',
                CLUB_CLASS.mutedOnDark,
                'desktop:text-white',
              )}
            >
              {t(price.key, price.params)}
            </p>
          </div>
        </section>
        <ul
          className={cn(
            'grid grid-cols-1 desktop:gap-4 gap-3',
            cards.length === 2 ? 'desktop:grid-cols-2' : 'desktop:grid-cols-3',
          )}
        >
          {cards.map((card) => (
            <li
              key={card.id}
              className={cn(
                'flex flex-col desktop:gap-2 gap-1.5 desktop:p-6 px-5 py-[18px]',
                CLUB_CLASS.card,
              )}
            >
              <h3 className="m-0 font-semibold desktop:text-[18px] text-[16px] leading-5">
                {t(card.title.key, card.title.params)}
              </h3>
              <p
                className={cn(
                  'desktop:text-[14px] text-[13px] desktop:leading-6 leading-5',
                  CLUB_CLASS.muted,
                )}
              >
                {t(card.text.key, card.text.params)}
              </p>
            </li>
          ))}
        </ul>
        <ClubPlanTerms plan={plan} formatters={formatters} />
      </div>
    );
  },
);
